import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    IconButton,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

interface GenericModalProps {
    open: boolean;
    onClose: () => void;
    title: React.ReactNode;
    subtitle?: string;
    children: React.ReactNode;
    actions?: React.ReactNode;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    fullWidth?: boolean;
}

const GenericModal: React.FC<GenericModalProps> = ({
    open,
    onClose,
    title,
    subtitle,
    children,
    actions,
    maxWidth = 'md',
    fullWidth = true
}) => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            PaperProps={{
                sx: {
                    bgcolor: theme.palette.background.paper,
                    borderRadius: isMobile ? 0 : 3,
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden' // Prevent dialog from scrolling
                }
            }}
        >
            {/* Fixed Header */}
            <DialogTitle sx={{
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
                pb: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0, // Prevent header from shrinking
                bgcolor: theme.palette.background.paper,
                zIndex: 1
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box>
                        {typeof title === 'string' ? (
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {title}
                            </Typography>
                        ) : (
                            title
                        )}
                        {subtitle && (
                            <Typography variant="body2" color="textSecondary">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: theme.palette.text.secondary,
                        '&:hover': {
                            color: theme.palette.text.primary,
                            bgcolor: theme.palette.action.hover
                        }
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>

            {/* Scrollable Content */}
            <DialogContent sx={{
                bgcolor: theme.palette.background.paper,
                pt: '16px !important',
                px: isMobile ? 2 : 3,
                pb: 2,
                flex: 1,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: theme.palette.background.default,
                },
                '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.divider,
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: theme.palette.text.secondary,
                }
            }}>
                {children}
            </DialogContent>

            {/* Fixed Footer */}
            {actions && (
                <DialogActions sx={{
                    p: isMobile ? 2 : 3,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    gap: 1,
                    flexDirection: isMobile ? 'column' : 'row',
                    flexShrink: 0, // Prevent footer from shrinking
                    bgcolor: theme.palette.background.paper,
                    zIndex: 1
                }}>
                    {actions}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default GenericModal;
